import { useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Dropdown, Typography } from "antd";
import type { MenuProps } from "antd";
import {
  Award, Building2, Calculator, Calendar, ChevronDown, ClipboardList, DollarSign, FileText, Grid3x3, Home, LogOut,
  MessageSquare, Monitor, Package, Phone, Plane, Settings as SettingsIcon, ShoppingBag, User, Users, Zap,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout, setAvatarUrl } from "../../store/authSlice";
import { getPermissions, roleLabel, PAGE_PERMISSION } from "../../utils/permissions";
import axiosClient from "../../api/axiosClient";
import { COMING_SOON_PATHS, comingSoonLabel } from "../../utils/comingSoon";
import "./AdminLayout.css";

const { Text } = Typography;

// Core, everyday work areas — stay as flat top-level links for every role.
const NAV_LINKS: { path: string; icon: React.ReactNode; label: string }[] = [
  { path: "/admin/calendar", icon: <Calendar size={15} />, label: "Calendar" },
  { path: "/admin/clients", icon: <Users size={15} />, label: "Clients" },
  { path: "/admin/sales", icon: <ShoppingBag size={15} />, label: "Sales" },
  { path: "/admin/products", icon: <Package size={15} />, label: "Products" },
  { path: "/admin/booth-clients", icon: <Users size={15} />, label: "My Booth Clients" },
];

// Everything else lives in the "Apps" mega-menu, matching the mockup's TopNav —
// only shown to roles that get the Apps button (admin/front_desk); other roles
// fall back to a flat Reports link if they have that one permission.
const APPS_MENU: { path: string; icon: React.ReactNode; label: string }[] = [
  { path: "/admin/reports", icon: <FileText size={16} />, label: "Reports" },
  { path: "/admin/payroll", icon: <DollarSign size={16} />, label: "Payroll" },
  { path: "/admin/accounting", icon: <Calculator size={16} />, label: "Accounting" },
  { path: "/admin/booth-rental", icon: <Home size={16} />, label: "Booth Rental" },
  { path: "/admin/walkin", icon: <Monitor size={16} />, label: "Walk-In Kiosk" },
  { path: "/admin/telephony", icon: <Phone size={16} />, label: "AI Concierge" },
  { path: "/admin/connect", icon: <MessageSquare size={16} />, label: "Client Connect" },
  { path: "/admin/forms", icon: <ClipboardList size={16} />, label: "Digital Forms" },
  { path: "/admin/waitlist", icon: <Zap size={16} />, label: "Auto Waitlist" },
  { path: "/admin/memberships", icon: <Award size={16} />, label: "Memberships" },
];

export default function AdminLayout() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAppSelector((s) => s.auth);
  const permissions = getPermissions(auth.role);

  // Auth guard
  useEffect(() => {
    if (!auth.accessToken) {
      navigate("/login", { replace: true });
    }
  }, [auth.accessToken, navigate]);

  // Load the current user's avatar for the header — the JWT itself doesn't carry it
  useEffect(() => {
    if (!auth.accessToken) return;
    axiosClient
      .get("/me")
      .then((res) => dispatch(setAvatarUrl(res.data?.avatar_url || null)))
      .catch(() => {});
  }, [auth.accessToken, dispatch]);

  // Permission guard — block direct navigation to pages the role can't see,
  // and to features that aren't functional yet.
  useEffect(() => {
    const permKey = PAGE_PERMISSION[location.pathname];
    if ((permKey && !permissions[permKey]) || COMING_SOON_PATHS.has(location.pathname)) {
      navigate("/admin/calendar", { replace: true });
    }
  }, [location.pathname, permissions, navigate]);

  function handleSignOut() {
    dispatch(logout());
    navigate("/login", { replace: true });
  }

  const isOwner = auth.role === "admin";
  const isBoothRenter = auth.role === "booth_renter";
  // Matches the mockup's TopNav: only Owner/Front Desk get the Apps mega-menu.
  const showApps = auth.role === "admin" || auth.role === "front_desk";

  const visibleAppsMenu = showApps
    ? APPS_MENU.filter((item) => {
        const permKey = PAGE_PERMISSION[item.path];
        return !permKey || permissions[permKey];
      })
    : [];
  const appsMenuItems: MenuProps["items"] = visibleAppsMenu.map((item) => {
    const comingSoon = COMING_SOON_PATHS.has(item.path);
    return {
      key: item.path,
      icon: item.icon,
      label: comingSoon ? comingSoonLabel(item.label) : item.label,
      disabled: comingSoon,
      onClick: () => navigate(item.path),
    };
  });

  // Roles without the Apps button (e.g. barber) still get a flat Reports link
  // if they have that permission — same fallback the mockup uses.
  const fallbackLinks = showApps
    ? []
    : [{ path: "/admin/reports", icon: <FileText size={15} />, label: "Reports" }].filter(
        (link) => permissions[PAGE_PERMISSION[link.path]]
      );

  const userMenuItems: MenuProps["items"] = [
    {
      key: "profile",
      label: "Profile Settings",
      icon: <User size={14} />,
      onClick: () => navigate(isOwner ? "/admin/settings?tab=myProfile" : "/admin/settings"),
    },
    ...(isOwner
      ? [{
          key: "account",
          label: "Account Settings",
          icon: <SettingsIcon size={14} />,
          onClick: () => navigate("/admin/settings"),
        }]
      : []),
    ...(isBoothRenter
      ? [{
          key: "booth-account",
          label: "Booth Account Settings",
          icon: <Home size={14} />,
          onClick: () => navigate("/admin/booth-settings"),
        }]
      : []),
    ...(permissions.canViewMultiLocation
      ? [{
          key: "multi-location",
          label: comingSoonLabel("Multi-Location HQ"),
          icon: <Building2 size={14} />,
          disabled: COMING_SOON_PATHS.has("/admin/locations"),
          onClick: () => navigate("/admin/locations"),
        }]
      : []),
    {
      key: "signout",
      label: "Sign Out",
      icon: <LogOut size={14} />,
      danger: true,
      onClick: handleSignOut,
    },
  ];

  return (
    <div>
      <header className="admin-top-nav">
        <Link to="/admin/calendar" className="admin-logo">
          <Plane size={22} className="admin-logo-icon" />
          <span>Hairplane</span>
        </Link>

        <nav className="admin-nav-links">
          {NAV_LINKS.filter((link) => {
            const permKey = PAGE_PERMISSION[link.path];
            return !permKey || permissions[permKey];
          }).map((link) => {
            const active = location.pathname.startsWith(link.path);
            return (
              <Link key={link.path} to={link.path} className={`admin-nav-link${active ? " active" : ""}`}>
                {link.icon}
                {link.label}
              </Link>
            );
          })}
          {fallbackLinks.map((link) => {
            const active = location.pathname.startsWith(link.path);
            return (
              <Link key={link.path} to={link.path} className={`admin-nav-link${active ? " active" : ""}`}>
                {link.icon}
                {link.label}
              </Link>
            );
          })}
        </nav>

        {visibleAppsMenu.length > 0 && (
          <Dropdown menu={{ items: appsMenuItems }} trigger={["click"]}>
            <div className="admin-nav-link" style={{ cursor: "pointer" }}>
              <Grid3x3 size={15} />
              Apps
              <ChevronDown size={12} />
            </div>
          </Dropdown>
        )}

        <Dropdown menu={{ items: userMenuItems }} trigger={["click"]}>
          <div className="admin-nav-user">
            {auth.avatarUrl
              ? <Avatar src={auth.avatarUrl} size={24} />
              : <User size={16} />}
            <div>
              <Text>{auth.identifier ?? "Account"}</Text>
              <div className="admin-nav-user-role">{roleLabel(auth.role)}</div>
            </div>
            <ChevronDown size={14} />
          </div>
        </Dropdown>
      </header>

      <Outlet />
    </div>
  );
}
